import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";

const STORAGE_KEY = "rivones_cookie_consent";

export default function CookieBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show only on first visit (no decision stored yet)
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Defer slightly so it doesn't fight with hero animations
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ value, ts: new Date().toISOString() })
      );
    } catch {
      /* ignore quota / privacy mode */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Aviso de cookies"
      data-testid="cookie-banner"
      className="fixed left-3 right-3 sm:left-6 sm:right-auto sm:max-w-md z-[60] bottom-[max(env(safe-area-inset-bottom),16px)] sm:bottom-6 mb-[88px] sm:mb-0 cookie-banner-enter"
    >
      <div className="rounded-2xl bg-[#111111] text-white shadow-[0_18px_60px_rgba(0,0,0,0.35)] border border-white/10 backdrop-blur p-5">
        <div className="flex items-start gap-3">
          <span
            className="inline-flex w-10 h-10 rounded-xl bg-white/10 items-center justify-center shrink-0"
            aria-hidden
          >
            <Cookie className="w-5 h-5 text-amber-300" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-semibold text-base leading-tight">
              {t("cookieBanner.title")}
            </p>
            <p className="text-sm text-white/70 mt-1.5 leading-relaxed">
              {t("cookieBanner.body")}{" "}
              <Link
                to="/privacidad"
                className="text-amber-300 hover:text-amber-200 underline underline-offset-2"
                data-testid="cookie-banner-privacy-link"
              >
                {t("footer.privacy")}
              </Link>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => persist("dismissed")}
            aria-label={t("common.close")}
            data-testid="cookie-banner-close"
            className="shrink-0 w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            onClick={() => persist("rejected")}
            data-testid="cookie-banner-reject"
            variant="outline"
            className="flex-1 h-10 rounded-xl bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"
          >
            {t("cookieBanner.reject")}
          </Button>
          <Button
            onClick={() => persist("accepted")}
            data-testid="cookie-banner-accept"
            className="flex-1 h-10 rounded-xl bg-white text-[#111111] hover:bg-neutral-100 font-semibold"
          >
            {t("cookieBanner.accept")}
          </Button>
        </div>
      </div>
    </div>
  );
}
